import React from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import {
  blue,
  white,
} from '@fortress-technology-solutions/fortress-component-library/design';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import { InputLabel } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { SetupLabel, SetupSwitch, SetupTitle } from '../Programs/shared';
import SetupGridItem from '../../SetupGridItem';
import messages from './messages';
import { useWaitlistV2, PREFERENCE_RANKING_METHOD } from './hooks';
import PreferenceRow from './PreferenceRow';
import ApplicationsNeedingInfoModal from './ApplicationsNeedingInfoModal';
import {
  ENABLE_WAITLIST_FIELD,
  useApplicationsNeedingInfo,
} from './ApplicationsNeedingInfoModal/hooks';
import type { Props } from './types';

const PREFERENCE_RANKING_OPTIONS = [
  { value: PREFERENCE_RANKING_METHOD.TOTAL_POINTS, label: 'Total Points' },
  {
    value: PREFERENCE_RANKING_METHOD.HIGHEST_PREFERENCE_WEIGHT,
    label: 'Highest Preference Weight',
  },
];

const selectSharedStyles = {
  valueContainer: (base) => ({
    ...base,
    backgroundColor: white,
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '14px',
    paddingRight: '14px',
    borderRadius: '8px',
  }),
  control: (base) => ({
    ...base,
    borderRadius: '8px',
  }),
};

function WaitlistV2({ editMode, onChange, values }: Props) {
  const {
    affordableProgramOptions,
    affordableWaitlistEnabled,
    affordableWaitlistOpen,
    separateByBedroom,
    usePreferences,
    preferenceWeightMethod,
    localPrograms,
    selectComponents,
    preferencesOptions,
    openDateValue,
    closeDateValue,
    closeDateMinDate,
    closeDateError,
    openDateError,
    openDateDisplay,
    closeDateDisplay,
    handleAffordableProgramsChange,
    handleOpenWaitlistChange,
    handleOpenDateChange,
    handleCloseDateChange,
    handleSeparateByBedroomChange,
    handleUsePreferencesChange,
    handlePreferenceWeightMethodChange,
    handlePreferenceChange,
  } = useWaitlistV2(values, onChange, editMode);

  const applicationsNeedingInfo = useApplicationsNeedingInfo(
    values.organizationId,
    values.propertyId,
    onChange,
    editMode,
  );

  return (
    <>
      <ApplicationsNeedingInfoModal
        open={applicationsNeedingInfo.isOpen}
        count={applicationsNeedingInfo.count}
        onContinue={applicationsNeedingInfo.handleContinue}
        onCancel={applicationsNeedingInfo.handleCancel}
      />
      <SetupGridItem
        xs={12}
        sm={6}
        label={<FormattedMessage {...messages.affordablePrograms} />}
        TooltipProps={{
          title: <FormattedMessage {...messages.affordableProgramsTooltip} />,
        }}
      >
        <Select
          isMulti
          isDisabled={!editMode}
          options={affordableProgramOptions}
          value={localPrograms}
          onChange={handleAffordableProgramsChange}
          styles={{
            ...selectSharedStyles,
            multiValue: (base) => ({
              ...base,
              backgroundColor: '#DBEBFF',
              color: blue.main,
            }),
            multiValueLabel: (base) => ({
              ...base,
              color: blue.main,
            }),
          }}
          components={selectComponents}
          placeholder=""
        />
      </SetupGridItem>

      <SetupGridItem
        xs={12}
        sm={6}
        label={<FormattedMessage {...messages.enableWaitlist} />}
        TooltipProps={{
          title: <FormattedMessage {...messages.enableWaitlistTooltip} />,
        }}
      >
        <SetupSwitch
          switchId={'waitlist-v2-enable-waitlist'}
          editMode={editMode}
          name={ENABLE_WAITLIST_FIELD}
          disabled={!editMode}
          value={affordableWaitlistEnabled}
          onChange={applicationsNeedingInfo.handleEnableWaitlistChange}
        />
      </SetupGridItem>

      {affordableWaitlistEnabled && (
        <>
          <SetupGridItem>
            <SetupLabel {...messages.openWaitlist} />
            <SetupSwitch
              switchId={'waitlist-v2-open-waitlist'}
              editMode={editMode}
              name="setup.affordableWaitlistStatus"
              disabled={!editMode}
              value={affordableWaitlistOpen}
              onChange={handleOpenWaitlistChange}
            />
          </SetupGridItem>

          <SetupGridItem
            xs={12}
            sm={6}
            label={<FormattedMessage {...messages.separateWaitlist} />}
          >
            <SetupSwitch
              switchId={'waitlist-v2-separate-by-bedroom'}
              editMode={editMode}
              name="setup.affordableWaitlistSeparateByBedroom"
              disabled={!editMode}
              value={separateByBedroom}
              onChange={handleSeparateByBedroomChange}
            />
          </SetupGridItem>

          <SetupGridItem
            xs={12}
            sm={6}
            label={<FormattedMessage {...messages.openDate} />}
          >
            {editMode ? (
              <DatePicker
                id="waitlist-v2-open-date"
                onChange={handleOpenDateChange}
                value={openDateValue}
                error={!!openDateError}
                helperText={openDateError}
              />
            ) : openDateDisplay ? (
              formatDateDisplayLocal(openDateDisplay)
            ) : (
              '---'
            )}
          </SetupGridItem>

          <SetupGridItem
            xs={12}
            sm={6}
            label={<FormattedMessage {...messages.closeDate} />}
          >
            {editMode ? (
              <DatePicker
                id="waitlist-v2-close-date"
                onChange={handleCloseDateChange}
                value={closeDateValue}
                disabled={!openDateValue}
                minDate={closeDateMinDate}
                error={!!closeDateError}
                helperText={closeDateError}
              />
            ) : closeDateDisplay ? (
              formatDateDisplayLocal(closeDateDisplay)
            ) : (
              '---'
            )}
          </SetupGridItem>

          <SetupGridItem
            xs={12}
            sm={6}
            label={<FormattedMessage {...messages.usePreferences} />}
            TooltipProps={{
              title: <FormattedMessage {...messages.usePreferencesTooltip} />,
            }}
          >
            <SetupSwitch
              switchId={'waitlist-v2-use-preferences'}
              editMode={editMode}
              name="setup.affordableWaitlistUsePreferences"
              disabled={!editMode}
              value={usePreferences}
              onChange={handleUsePreferencesChange}
            />
          </SetupGridItem>

          <SetupGridItem
            xs={12}
            sm={6}
            label={<FormattedMessage {...messages.preferencesRankingMethod} />}
            TooltipProps={{
              title: (
                <FormattedMessage
                  {...messages.preferencesRankingMethodTooltip}
                />
              ),
            }}
          >
            <Select
              isDisabled={!editMode || !usePreferences}
              options={PREFERENCE_RANKING_OPTIONS}
              value={
                PREFERENCE_RANKING_OPTIONS.find(
                  (o) => o.value === preferenceWeightMethod,
                ) ?? null
              }
              onChange={handlePreferenceWeightMethodChange}
              styles={{ ...selectSharedStyles }}
              placeholder=""
            />
          </SetupGridItem>
          <SetupTitle>
            <FormattedMessage {...messages.configurePreferencesTitle} />
          </SetupTitle>
          <SetupGridItem xs={12} sm={12}>
            <Grid container>
              <Grid item xs={9}>
                <InputLabel>
                  <FormattedMessage {...messages.preferencesSection} />
                </InputLabel>
              </Grid>
              <Grid item xs={3}>
                <InputLabel>
                  <FormattedMessage {...messages.rankingWeightSection} />
                </InputLabel>
              </Grid>
            </Grid>

            {preferencesOptions.map((option) => (
              <PreferenceRow
                key={option.id}
                option={option}
                editMode={editMode}
                usePreferences={usePreferences}
                handlePreferenceChange={handlePreferenceChange}
              />
            ))}
          </SetupGridItem>
        </>
      )}
    </>
  );
}

export default WaitlistV2;
